import React, { PropsWithChildren, useEffect, useMemo, useState } from "react";
import { ToggleEnableActionWrapper } from "./Configuration/OtherConfigurationItems/ToggleEnableActionWrapper";
import { ReceivablesAgingGraphScale } from "./Configuration/ReceivablesAgingGraphScale";
import { PriorityIntervals } from "./Configuration/PriorityIntervals";
import { PriorityFormulaWeights } from "./Configuration/PriorityFormulaWeights";
import { PasswordConfiguration } from "./Configuration/PasswordConfiguration";
import { TotalSegmentPriorities } from "./Configuration/TotalSegmentPriorities";
import { CreateActivityWhenInvoiceStatusChange } from "./Configuration/CreateActivityWhenInvoiceStatusChange";
import { EnableCustomerMessages } from "./Configuration/EnableCustomerMessages";
import { InboundMailsActivityType } from "./Configuration/InboundMailsActivityType";
import { DefaultFieldForSMS } from "./Configuration/DefaultFieldForSMS";
import { DefaultFieldForWhatsapp } from "./Configuration/DefaultFieldForWhatsapp";
import { DefaultOrderOfInvoicesInEmailsAndLetters } from "./Configuration/DefaultOrderOfInvoicesInEmailsAndLetters";
import { PaymentMethods } from "./Configuration/PaymentMethods";
import { BackupConfiguration } from "./Configuration/BackupConfiguration";
import { ReceiptTemplateConfiguration } from "./Configuration/ReceiptTemplateConfiguration";
import { CustomerPortalLinkExpirationTime } from "./Configuration/CustomerPortalLinkExpirationTime";
import CompanyService from "../../services/CompanyService";
import { GroupInvoicesinEmailsandLetters } from "./Configuration/GroupInvoicesinEmailsandLetters";
import { TranslationService } from "../../services/TranslationService";
import { AutomaticActionsLanguage } from "./Configuration/OtherConfigurationItems/AutomaticActionsLanguage";
import { TextLinkPortal } from "./Configuration/TextLinkPortal";
import { FinalStatusPaymentLink } from "./Configuration/OtherConfigurationItems/FinalStatusPaymentLink";
import { UnlimitedSMSConfiguration } from "./Configuration/OtherConfigurationItems/UnlimitedSMSConfiguration";
import { CompanySettings } from "../../entities/company/Company/CompanySettings";

type ConfigurationObjetItemType = {
  field: number;
  key?: string;
  name: string;
  value: string;
  IncludeInList: boolean;
  IncludeInMail: boolean;
  ShowAlways: boolean;
  IncludeInClientExport: boolean;
  Entity: number;
  ManualEdit: boolean;
  IncludeMainCurrency: boolean;
  ShowDueDays: boolean;
  ExcludeInFilters: boolean;
  Whatsapp: boolean;
  IncludeInCustomerPortal: boolean;
};

type otherConfigurationListType = {
  titleGroup: string;
  childrenGroup: (
    | {
        name: string;
        component: () => JSX.Element;
      }
    | {
        name: string;
        component: null;
      }
  )[];
};

type ConfigurationContextType = {
  otherConfigurationList?: otherConfigurationListType[];
  configValues: ConfigurationObjetItemType[] | [];
  refreshConfigValues: () => void;
};

const ConfigurationContext = React.createContext(
  {} as ConfigurationContextType
);

export const ConfigurationProvider = ({ children }: PropsWithChildren<any>) => {
  const [configValues, setConfigValues] = useState<
    ConfigurationObjetItemType[] | []
  >([]);
  const hasReceipt =
    CompanyService.canDo("hasReceipt") && CompanyService.canShowReceipt();

  const keyValueChecked = useMemo(
    () =>
      configValues?.length > 0
        ? configValues
            .map(({ name, value }: any) => ({ [name]: value }))
            .reduce((obj: any, item: any) => {
              obj = { ...obj, ...item };
              return obj;
            }, {})
        : undefined,
    [configValues]
  );

  const otherConfigurationList = useMemo(() => {
    let optionsValues = undefined;
    const inHouse =
      CompanyService.isBackOfficeUser() || CompanyService.inHouse();
    const otherChildrenGroup = [];
    otherChildrenGroup.push({
      name: "IncludeSegmentsInCustomerExports",
      component: () => (
        <ToggleEnableActionWrapper
          title="IncludeSegmentsInCustomerExports"
          action="exportsegment"
          initialValue={keyValueChecked["exportsegment"]}
        />
      )
    });

    otherChildrenGroup.push({
      name: "EnableCustomerGroups",
      component: () => (
        <ToggleEnableActionWrapper
          title="EnableCustomerGroups"
          action="multiplekey"
          initialValue={keyValueChecked["multiplekey"]}
        />
      )
    });

    otherChildrenGroup.push({
      name: "EnableTaskValidator",
      component: () => (
        <ToggleEnableActionWrapper
          title="EnableTaskValidator"
          action="taskvalidator"
          initialValue={keyValueChecked["taskvalidator"]}
        />
      )
    });

    otherChildrenGroup.push({
      name: "EnabledProgrammedTasks",
      component: () => (
        <ToggleEnableActionWrapper
          title="EnabledProgrammedTasks"
          action="taskscheduler"
          initialValue={keyValueChecked["taskscheduler"]}
        />
      )
    });

    otherChildrenGroup.push({
      name: "EnableActivitiesAndInvoices",
      component: () => (
        <ToggleEnableActionWrapper
          title="EnableActivitiesAndInvoices"
          action="messageiolink"
          initialValue={keyValueChecked["messageiolink"]}
        />
      )
    });

    otherChildrenGroup.push({
      name: "RegisterAutomateActionsToEmails",
      component: () => (
        <ToggleEnableActionWrapper
          title="RegisterAutomateActionsToEmails"
          action="registerallactivities"
          initialValue={keyValueChecked["registerallactivities"]}
        />
      )
    });

    otherChildrenGroup.push({
      name: "MandatoryActivityTypeInComments",
      component: () => (
        <ToggleEnableActionWrapper
          title="MandatoryActivityTypeInComments"
          action="mandatorycommenttype"
          initialValue={keyValueChecked["mandatorycommenttype"]}
        />
      )
    });

    otherChildrenGroup.push({
      name: "MandatoryActivityTypeInEmails",
      component: () => (
        <ToggleEnableActionWrapper
          title="MandatoryActivityTypeInEmails"
          action="mandatoryemailtype"
          initialValue={keyValueChecked["mandatoryemailtype"]}
        />
      )
    });

    otherChildrenGroup.push({
      name: "EnableImportOfCustomerAndInvoiceDataByCustomField",
      component: () => (
        <ToggleEnableActionWrapper
          title="EnableImportOfCustomerAndInvoiceDataByCustomField"
          action="customfieldpersonimport"
          initialValue={keyValueChecked["customfieldpersonimport"]}
        />
      )
    });

    otherChildrenGroup.push({
      name: "MandatoryActivityTypeInTasks",
      component: () => (
        <ToggleEnableActionWrapper
          title="MandatoryActivityTypeInTasks"
          action="mandatorytasktype"
          initialValue={keyValueChecked["mandatorytasktype"]}
        />
      )
    });

    if (inHouse) {
      otherChildrenGroup.push({
        name: "MandatoryInvoiceListingInComments",
        component: () => (
          <ToggleEnableActionWrapper
            title="MandatoryInvoiceListingInComments"
            action="mandatoryiocomment"
            initialValue={keyValueChecked["mandatoryiocomment"]}
          />
        )
      });
    }

    otherChildrenGroup.push({
      name: "MandatoryTagInTasks",
      component: () => (
        <ToggleEnableActionWrapper
          title="MandatoryTagInTasks"
          action="requiredTagInTask"
          initialValue={keyValueChecked["requiredTagInTask"]}
        />
      )
    });

    otherChildrenGroup.push({
      name: "InboundMailsActivityType",
      component: () => (
        <InboundMailsActivityType
          initialValue={keyValueChecked["inboundactivitytypeid"]}
        />
      )
    });

    otherChildrenGroup.push({
      name: "MandatoryTagInComments",
      component: () => (
        <ToggleEnableActionWrapper
          title="MandatoryTagInComments"
          action="mandatorytag"
          initialValue={keyValueChecked["mandatorytag"]}
        />
      )
    });

    otherChildrenGroup.push({
      name: "Max1TagInComments",
      component: () => (
        <ToggleEnableActionWrapper
          title="Max1TagInComments"
          action="only1tag"
          initialValue={keyValueChecked["only1tag"]}
        />
      )
    });

    otherChildrenGroup.push({
      name: "CanSelectCommentDate",
      component: () => (
        <ToggleEnableActionWrapper
          title="CanSelectCommentDate"
          action="canselectactivitydate"
          initialValue={keyValueChecked["canselectactivitydate"]}
        />
      )
    });

    otherChildrenGroup.push({
      name: "IncludeLastActivityInCustomerReport",
      component: () => (
        <ToggleEnableActionWrapper
          title="IncludeLastActivityInCustomerReport"
          action="includelastlogclient"
          initialValue={keyValueChecked["includelastlogclient"]}
        />
      )
    });

    otherChildrenGroup.push({
      name: "IncludeLastActivityInInvoiceReport",
      component: () => (
        <ToggleEnableActionWrapper
          title="IncludeLastActivityInInvoiceReport"
          action="includelastlogio"
          initialValue={keyValueChecked["includelastlogio"]}
        />
      )
    });

    otherChildrenGroup.push({
      name: "IncludeLastActivityInPaymentsReport",
      component: () => (
        <ToggleEnableActionWrapper
          title="IncludeLastActivityInPaymentsReport"
          action="includelastlogtransaction"
          initialValue={keyValueChecked["includelastlogtransaction"]}
        />
      )
    });

    otherChildrenGroup.push({
      name: "IncludeUserInPaymentReport",
      component: () => (
        <ToggleEnableActionWrapper
          title="IncludeUserInPaymentReport"
          action="includeuserintransactionreport"
          initialValue={keyValueChecked["includeuserintransactionreport"]}
        />
      )
    });

    otherChildrenGroup.push({
      name: "MandatoryFileUploadInEffectiveActivities",
      component: () => (
        <ToggleEnableActionWrapper
          title="MandatoryFileUploadInEffectiveActivities"
          action="mandatoryeffectiveactivityfile"
          initialValue={keyValueChecked["mandatoryeffectiveactivityfile"]}
        />
      )
    });

    otherChildrenGroup.push({
      name: "CreateActivityWhenInvoiceStatusChange",
      component: () => (
        <CreateActivityWhenInvoiceStatusChange
          iostatuscomment={keyValueChecked["iostatuscomment"]}
          iostatuscommentactivitytype={
            keyValueChecked["iostatuscommentactivitytype"]
          }
        />
      )
    });

    otherChildrenGroup.push({
      name: "ShowTotalsInCutomersCurrency",
      component: () => (
        <ToggleEnableActionWrapper
          title="ShowTotalsInCutomersCurrency"
          action="showtotalincustomercurrency"
          initialValue={keyValueChecked["showtotalincustomercurrency"]}
        />
      )
    });

    otherChildrenGroup.push({
      name: "IncludeCustomerDataInInvoiceListHeader",
      component: () => (
        <ToggleEnableActionWrapper
          title="IncludeCustomerDataInInvoiceListHeader"
          action="includepersontable"
          initialValue={keyValueChecked["includepersontable"]}
        />
      )
    });

    otherChildrenGroup.push({
      name: "IncludePendingTasksAsActivity",
      component: () => (
        <ToggleEnableActionWrapper
          title="IncludePendingTasksAsActivity"
          action="lastActivityPendingTask"
          initialValue={keyValueChecked["lastActivityPendingTask"]}
        />
      )
    });

    otherChildrenGroup.push({
      name: "IncludeLogoInMails",
      component: () => (
        <ToggleEnableActionWrapper
          title="IncludeLogoInMails"
          action="includelogoinmails"
          initialValue={keyValueChecked["includelogoinmails"]}
        />
      )
    });

    otherChildrenGroup.push({
      name: "EnableMailsWithFormat",
      component: () => (
        <ToggleEnableActionWrapper
          title="EnableMailsWithFormat"
          action="htmlmails"
          initialValue={keyValueChecked["htmlmails"]}
        />
      )
    });

    otherChildrenGroup.push({
      name: "ShowUntypedInteractionsEffective",
      component: () => (
        <ToggleEnableActionWrapper
          title="ShowUntypedInteractionsEffective"
          action="noactivitytypeeffective"
          initialValue={keyValueChecked["noactivitytypeeffective"]}
        />
      )
    });

    if (inHouse) {
      otherChildrenGroup.push({
        name: "NotifyDirectEmailsFollowUp",
        component: () => (
          <ToggleEnableActionWrapper
            title="NotifyDirectEmailsFollowUp"
            action="notifydirectemails"
            initialValue={keyValueChecked["notifydirectemails"]}
          />
        )
      });
    }

    if (inHouse) {
      otherChildrenGroup.push({
        name: "IncludePendingAmountReceipts",
        component: () => (
          <ToggleEnableActionWrapper
            title="IncludePendingAmountReceipts"
            action="includependinginreceipt"
            initialValue={keyValueChecked["includependinginreceipt"]}
          />
        )
      });
    }

    otherChildrenGroup.push({
      name: "GroupTotalsCurrencySelectingInvoices",
      component: () => (
        <ToggleEnableActionWrapper
          title="GroupTotalsCurrencySelectingInvoices"
          action="groupediototals"
          initialValue={keyValueChecked["groupediototals"]}
        />
      )
    });

    otherChildrenGroup.push({
      name: "PriorityBySegments",
      component: () => (
        <ToggleEnableActionWrapper
          title="PriorityBySegments"
          action="enablesegmentpriority"
          initialValue={keyValueChecked["enablesegmentpriority"]}
        />
      )
    });

    otherChildrenGroup.push({
      name: "TotalSegmentPriorities",
      component: () => (
        <TotalSegmentPriorities
          initialValue={keyValueChecked["totalsegmentpriorities"]}
        />
      )
    });

    otherChildrenGroup.push({
      name: "EnableCustomerPortal",
      component: () => (
        <ToggleEnableActionWrapper
          initialValue={keyValueChecked["customerportalenabled"]}
          title="EnableCustomerPortal"
          action="customerportalenabled"
        />
      )
    });

    otherChildrenGroup.push({
      name: "ShowInvoiceHistory",
      component: () => (
        <ToggleEnableActionWrapper
          title="ShowInvoiceHistory"
          action="customerportalshowhistory"
          initialValue={keyValueChecked["customerportalshowhistory"]}
        />
      )
    });

    otherChildrenGroup.push({
      name: "ShowOnlyPositiveInvoicesByDefault",
      component: () => (
        <ToggleEnableActionWrapper
          title="ShowOnlyPositiveInvoicesByDefault"
          action="customerportalonlypositive"
          initialValue={keyValueChecked["customerportalonlypositive"]}
        />
      )
    });

    otherChildrenGroup.push({
      name: "EnableCustomerMessages",
      component: () => (
        <EnableCustomerMessages
          customerMessages={keyValueChecked["customerMessages"]}
          customermessageactivitytypeid={
            keyValueChecked["customermessageactivitytypeid"]
          }
          customerMessagesTo={keyValueChecked["customerMessagesTo"]}
          customerMessagesToAddress={
            keyValueChecked["customerMessagesToAddress"]
          }
          customerMessagesToAdditional={
            keyValueChecked["customerMessagesToAdditional"]
          }
        />
      )
    });

    otherChildrenGroup.push({
      name: "DefaultFieldForSMS",
      component: () => (
        <DefaultFieldForSMS initialValue={keyValueChecked["defaultsmsfield"]} />
      )
    });

    otherChildrenGroup.push({
      name: "DefaultFieldForWhatsapp",
      component: () => (
        <DefaultFieldForWhatsapp
          initialValue={keyValueChecked["defaultwafield"]}
        />
      )
    });

    otherChildrenGroup.push({
      name: "LockTheStatusOfACompletedTask",
      component: () => (
        <ToggleEnableActionWrapper
          title="LockTheStatusOfACompletedTask"
          action="taskdonelock"
          initialValue={keyValueChecked["taskdonelock"]}
        />
      )
    });

    if (CompanyService.isBackOfficeUser()) {
      otherChildrenGroup.push({
        name: "DirectWhatsappMessage",
        component: () => (
          <ToggleEnableActionWrapper
            title="DirectWhatsappMessage"
            action="directWhatsappMessage"
            initialValue={keyValueChecked["directWhatsappMessage"]}
          />
        )
      });
    }

    if (CompanyService.isBackOfficeUser()) {
      otherChildrenGroup.push({
        name: "AllowWhatsappChatbot",
        component: () => (
          <ToggleEnableActionWrapper
            title="AllowWhatsappChatbot"
            action="allowWhatsappChatbot"
            initialValue={keyValueChecked["allowWhatsappChatbot"]}
          />
        )
      });
    }

    if (CompanyService.isBackOfficeUser()) {
      otherChildrenGroup.push({
        name: "AllowPaymentProvider",
        component: () => (
          <ToggleEnableActionWrapper
            title="AllowPaymentProvider"
            action="allowPaymentProvider"
            initialValue={keyValueChecked["allowPaymentProvider"]}
          />
        )
      });
    }

    if (CompanyService.isBackOfficeUser()) {
      otherChildrenGroup.push({
        name: "AllowSuperSegment",
        component: () => (
          <ToggleEnableActionWrapper
            title="AllowSuperSegment"
            action="allowSuperSegment"
            initialValue={keyValueChecked["allowSuperSegment"]}
          />
        )
      });
    }

    if (inHouse) {
      otherChildrenGroup.push({
        name: "NotifyEnable",
        component: () => (
          <ToggleEnableActionWrapper
            title="NotifyEnable"
            action="notifymails"
            initialValue={keyValueChecked["notifymails"]}
          />
        )
      });
    }

    if (inHouse) {
      otherChildrenGroup.push({
        name: "SyncEnableBalance",
        component: () => (
          <ToggleEnableActionWrapper
            title="SyncEnableBalance"
            action="partialiosync"
            initialValue={keyValueChecked["partialiosync"]}
          />
        )
      });
    }

    if (inHouse) {
      otherChildrenGroup.push({
        name: "EnableImportsDictionaries",
        component: () => (
          <ToggleEnableActionWrapper
            title="EnableImportsDictionaries"
            action="allowimportdictionary"
            initialValue={keyValueChecked["allowimportdictionary"]}
          />
        )
      });
    }

    otherChildrenGroup.push({
      name: "EnableInvoiceDataUpdateNoCustomer",
      component: () => (
        <ToggleEnableActionWrapper
          title="EnableInvoiceDataUpdateNoCustomer"
          action="ioupdatebyref"
          initialValue={keyValueChecked["ioupdatebyref"]}
        />
      )
    });

    otherChildrenGroup.push({
      name: "EnableAdditionalPaymentFields",
      component: () => (
        <ToggleEnableActionWrapper
          title="EnableAdditionalPaymentFields"
          action="allowtransactionadditional"
          initialValue={keyValueChecked["allowtransactionadditional"]}
        />
      )
    });

    if (inHouse) {
      otherChildrenGroup.push({
        name: "EnablePreviousImports",
        component: () => (
          <ToggleEnableActionWrapper
            title="EnablePreviousImports"
            action="allowimportpredecessor"
            initialValue={keyValueChecked["allowimportpredecessor"]}
          />
        )
      });
    }

    otherChildrenGroup.push({
      name: "CustomerPortalLinkExpirationTime",
      component: () => (
        <CustomerPortalLinkExpirationTime
          initialValue={keyValueChecked["customersiteexpiredays"]}
        />
      )
    });

    otherChildrenGroup.push({
      name: "AllowGuestUsersSegmentAccessAllCustomers",
      component: () => (
        <ToggleEnableActionWrapper
          title="AllowGuestUsersSegmentAccessAllCustomers"
          action="allowsegmentfiltertoggle"
          initialValue={keyValueChecked["allowsegmentfiltertoggle"]}
        />
      )
    });

    otherChildrenGroup.push({
      name: "EnableManualEmailSenderEdit",
      component: () => (
        <ToggleEnableActionWrapper
          title="EnableManualEmailSenderEdit"
          action="allowemailfromedit"
          initialValue={keyValueChecked["allowemailfromedit"]}
        />
      )
    });

    otherChildrenGroup.push({
      name: "ShowLastInvoiceUpdateDateMain",
      component: () => (
        <ToggleEnableActionWrapper
          title="ShowLastInvoiceUpdateDateMain"
          action="enableshowlastimport"
          initialValue={keyValueChecked["enableshowlastimport"]}
        />
      )
    });

    otherChildrenGroup.push({
      name: "ShowUntypedInteractionsAsEffective",
      component: () => (
        <ToggleEnableActionWrapper
          title="ShowUntypedInteractionsAsEffective"
          action="noactivitytypeeffective"
          initialValue={keyValueChecked["noactivitytypeeffective"]}
        />
      )
    });

    otherChildrenGroup.push({
      name: "IncludeNonEffectiveInteractionsInLastInteraction",
      component: () => (
        <ToggleEnableActionWrapper
          invertValue
          title="IncludeNonEffectiveInteractionsInLastInteraction"
          action="lastLogEffective"
          initialValue={keyValueChecked["lastLogEffective"]}
        />
      )
    });

    otherChildrenGroup.push({
      name: "IncludeAutomaticInteractionsInLastInteraction",
      component: () => (
        <ToggleEnableActionWrapper
          title="IncludeAutomaticInteractionsInLastInteraction"
          action="lastLogAuto"
          initialValue={keyValueChecked["lastLogAuto"]}
        />
      )
    });

    otherChildrenGroup.push({
      name: "GroupedTotalsByCurrencyWhenSelectingInvoices",
      component: () => (
        <ToggleEnableActionWrapper
          title="GroupedTotalsByCurrencyWhenSelectingInvoices"
          action="groupediototals"
          initialValue={keyValueChecked["groupediototals"]}
        />
      )
    });

    otherChildrenGroup.push({
      name: "DefaultOrderOfInvoicesInEmailsAndLetters",
      component: () => (
        <DefaultOrderOfInvoicesInEmailsAndLetters
          ordermailby={keyValueChecked["ordermailby"]}
          ordermailbyorderdesc={keyValueChecked["ordermailbyorderdesc"]}
        />
      )
    });

    if (inHouse) {
      otherChildrenGroup.push({
        name: "EnableScheduledManagementReports",
        component: () => (
          <ToggleEnableActionWrapper
            title="EnableScheduledManagementReports"
            action="allowscheduledreports"
            initialValue={keyValueChecked["allowscheduledreports"]}
          />
        )
      });
    }

    if (inHouse) {
      otherChildrenGroup.push({
        name: "EnableFieldEditingAutoActions",
        component: () => (
          <ToggleEnableActionWrapper
            title="EnableFieldEditingAutoActions"
            action="allowactionadditionaldefinition"
            initialValue={keyValueChecked["allowactionadditionaldefinition"]}
          />
        )
      });
    }

    otherChildrenGroup.push({
      name: "DescendingOrderZA",
      component: () => (
        <ToggleEnableActionWrapper
          title="DescendingOrderZA"
          action="groupbyorderdesc"
          initialValue={keyValueChecked["groupbyorderdesc"]}
        />
      )
    });

    otherChildrenGroup.push({
      name: "ShowNegativeAmountsRed",
      component: () => (
        <ToggleEnableActionWrapper
          title="ShowNegativeAmountsRed"
          action="iosnegativeamountinred"
          initialValue={keyValueChecked["iosnegativeamountinred"]}
        />
      )
    });

    if (inHouse) {
      otherChildrenGroup.push({
        name: "GroupInvoicesinEmailsandLetters",
        component: () => (
          <GroupInvoicesinEmailsandLetters
            alloweditgroupmailby={keyValueChecked["alloweditgroupmailby"]}
            groupmailby={keyValueChecked["groupmailby"]}
            groupbyorderdesc={keyValueChecked["groupbyorderdesc"]}
          />
        )
      });
    }

    if (inHouse) {
      otherChildrenGroup.push({
        name: "PaymentTypeMandatoryField",
        component: () => (
          <ToggleEnableActionWrapper
            title="PaymentTypeMandatoryField"
            action="transactiontyperequired"
            initialValue={keyValueChecked["transactiontyperequired"]}
          />
        )
      });
    }

    if (inHouse) {
      otherChildrenGroup.push({
        name: "UnlimitedLengthSMS",
        component: () => (
          <UnlimitedSMSConfiguration
            initialUnlimitedValue={keyValueChecked["unlimitedsms"]}
            initialLongSMSValue={
              keyValueChecked["longSMS"]
                ? parseInt(keyValueChecked["longSMS"])
                : undefined
            }
          />
        )
      });
    }

    otherChildrenGroup.push({
      name: "HideDecimals",
      component: () => (
        <ToggleEnableActionWrapper
          title="HideDecimals"
          action="hidedecimals"
          initialValue={keyValueChecked["hidedecimals"]}
        />
      )
    });

    if (inHouse) {
      otherChildrenGroup.push({
        name: "EnableDeletedUsersInteractionFilter",
        component: () => (
          <ToggleEnableActionWrapper
            title="EnableDeletedUsersInteractionFilter"
            action="deletedusersfilter"
            initialValue={keyValueChecked["deletedusersfilter"]}
          />
        )
      });
    }

    if (inHouse) {
      otherChildrenGroup.push({
        name: "DeleteValuesInFieldsAssociatedWithInvoiceStatusOnChange",
        component: () => (
          <ToggleEnableActionWrapper
            title="DeleteValuesInFieldsAssociatedWithInvoiceStatusOnChange"
            action="cleariostatusadditionalfields"
            initialValue={keyValueChecked["cleariostatusadditionalfields"]}
          />
        )
      });
    }

    if (CompanyService.getGroupName()?.length > 0) {
      otherChildrenGroup.push({
        name: "ShowInsuredTable",
        component: () => (
          <ToggleEnableActionWrapper
            titleReplace={TranslationService.translate.ShowInsuredTable.replace(
              "{1}",
              CompanyService.getGroupName()
            )}
            title={CompanyService.getGroupName()}
            action="showgrouptable"
            initialValue={keyValueChecked["showgrouptable"]}
          />
        )
      });
    }

    if (keyValueChecked) {
      optionsValues = [
        {
          titleGroup: "AutomaticActions",
          childrenGroup: [
            {
              name: "AllowAutoActionsToSendEmailsToCustomers",
              component: () => (
                <ToggleEnableActionWrapper
                  title="AllowAutoActionsToSendEmailsToCustomers"
                  initialValue={keyValueChecked["actionsFull"]}
                  action="actionsFull"
                />
              )
            },
            {
              name: "ReplyMailsViaIntiza",
              component: () => (
                <ToggleEnableActionWrapper
                  title="ReplyMailsViaIntiza"
                  initialValue={keyValueChecked["replyintiza"]}
                  action="replyintiza"
                />
              )
            },
            {
              name: "AutomaticActionsLanguage",
              component: () => (
                <AutomaticActionsLanguage
                  title="AutomaticActionsLanguage"
                  action="actionsCulture"
                  initialValue={keyValueChecked["actionsCulture"]}
                  items={[
                    {
                      value: null,
                      text: TranslationService.translate.SameCreator
                    },
                    { value: "es", text: "Español" },
                    { value: "en", text: "English" },
                    { value: "pt", text: "Portuguese" }
                  ]}
                />
              )
            },
            {
              name: "CustomerPortalLinkTextInEmails",
              component: () => (
                <TextLinkPortal linkPortal={keyValueChecked["linkPortal"]} />
              )
            }
          ]
        },
        {
          titleGroup: "Dashboard",
          childrenGroup: [
            {
              name: "ReceivablesAgingGraphScale",
              component: () => (
                <ReceivablesAgingGraphScale
                  initialValue={keyValueChecked["graphscales"]}
                />
              )
            },
            {
              name: "PriorityIntervals",
              component: () => (
                <PriorityIntervals
                  initialValue1={keyValueChecked["rank1"]}
                  initialValue2={keyValueChecked["rank2"]}
                />
              )
            },
            {
              name: "PriorityFormulaWeights",
              component: () => (
                <PriorityFormulaWeights
                  amountRankPercentValue={keyValueChecked["amountRankPercent"]}
                  dueRankPercentValue={keyValueChecked["dueRankPercent"]}
                  lastActivityRankPercentValue={
                    keyValueChecked["lastActivityRankPercent"]
                  }
                  lastTaskRankPercentValue={
                    keyValueChecked["lastTaskRankPercent"]
                  }
                />
              )
            }
          ]
        },
        {
          titleGroup: "ActionButtons",
          childrenGroup: [
            {
              name: "EditInvoices",
              component: () => (
                <ToggleEnableActionWrapper
                  title="EditInvoices"
                  invertValue
                  action="hideeditio"
                  initialValue={keyValueChecked["hideeditio"]}
                />
              )
            },
            {
              name: "CreateInvoices",
              component: () => (
                <ToggleEnableActionWrapper
                  title="CreateInvoices"
                  invertValue
                  action="hidecreateio"
                  initialValue={keyValueChecked["hidecreateio"]}
                />
              )
            },
            {
              name: "EditCustomers",
              component: () => (
                <ToggleEnableActionWrapper
                  title="EditCustomers"
                  invertValue
                  action="hideeditperson"
                  initialValue={keyValueChecked["hideeditperson"]}
                />
              )
            },
            {
              name: "EditPayments",
              component: () => (
                <ToggleEnableActionWrapper
                  title="EditPayments"
                  invertValue
                  action="hideeditpayment"
                  initialValue={keyValueChecked["hideeditpayment"]}
                />
              )
            },
            {
              name: "EnablePaymentInMultiCurrency",
              component: () => (
                <ToggleEnableActionWrapper
                  title="EnablePaymentInMultiCurrency"
                  action="enablemulticurrencytransaction"
                  initialValue={
                    keyValueChecked["enablemulticurrencytransaction"]
                  }
                />
              )
            },
            {
              name: "CreateCustomers",
              component: () => (
                <ToggleEnableActionWrapper
                  title="CreateCustomers"
                  invertValue
                  action="hidecreateperson"
                  initialValue={keyValueChecked["hidecreateperson"]}
                />
              )
            },
            {
              name: "ImportInvoices",
              component: () => (
                <ToggleEnableActionWrapper
                  title="ImportInvoices"
                  invertValue
                  action="hideioimport"
                  initialValue={keyValueChecked["hideioimport"]}
                />
              )
            },
            {
              name: "ImportNewInvoices",
              component: () => (
                <ToggleEnableActionWrapper
                  title="ImportNewInvoices"
                  invertValue
                  action="hidenewioimport"
                  initialValue={keyValueChecked["hidenewioimport"]}
                />
              )
            },
            {
              name: "UpdateMultipleInvoices",
              component: () => (
                <ToggleEnableActionWrapper
                  title="UpdateMultipleInvoices"
                  invertValue
                  action="hideupdateioimport"
                  initialValue={keyValueChecked["hideupdateioimport"]}
                />
              )
            },
            {
              name: "ImportCustomers",
              component: () => (
                <ToggleEnableActionWrapper
                  title="ImportCustomers"
                  invertValue
                  action="hidepersonimport"
                  initialValue={keyValueChecked["hidepersonimport"]}
                />
              )
            },
            {
              name: "ImportPayments",
              component: () => (
                <ToggleEnableActionWrapper
                  title="ImportPayments"
                  invertValue
                  action="hidetransactionimport"
                  initialValue={keyValueChecked["hidetransactionimport"]}
                />
              )
            },
            {
              name: "ImportComments",
              component: () => (
                <ToggleEnableActionWrapper
                  title="ImportComments"
                  invertValue
                  action="hideactivityimport"
                  initialValue={keyValueChecked["hideactivityimport"]}
                />
              )
            },
            {
              name: "ImportFiles",
              component: () => (
                <ToggleEnableActionWrapper
                  title="ImportFiles"
                  invertValue
                  action="hidefileimport"
                  initialValue={keyValueChecked["hidefileimport"]}
                />
              )
            },
            {
              name: "DeleteMultipleCustomers",
              component: () => (
                <ToggleEnableActionWrapper
                  title="DeleteMultipleCustomers"
                  invertValue
                  action="hidedeletepersonlist"
                  initialValue={keyValueChecked["hidedeletepersonlist"]}
                />
              )
            },
            {
              name: "EnableMultipleInvoiceRemoval",
              component: () => (
                <ToggleEnableActionWrapper
                  title="EnableMultipleInvoiceRemoval"
                  action="enableiodelete"
                  initialValue={keyValueChecked["enableiodelete"]}
                />
              )
            },
            {
              name: "EnableAutomaticInvoiceUpdate",
              component: () => (
                <ToggleEnableActionWrapper
                  title="EnableAutomaticInvoiceUpdate"
                  action="enableioautomaticupdate"
                  initialValue={keyValueChecked["enableioautomaticupdate"]}
                />
              )
            },
            {
              name: "EnableAutomaticCustomerUpdate",
              component: () => (
                <ToggleEnableActionWrapper
                  title="EnableAutomaticCustomerUpdate"
                  action="enablepersonautomaticupdate"
                  initialValue={keyValueChecked["enablepersonautomaticupdate"]}
                />
              )
            },
            {
              name: "finalstatuspaymentlink",
              component: () => (
                <FinalStatusPaymentLink
                  title="finalstatuspaymentlink"
                  action={"finalstatuspaymentlink" as keyof CompanySettings}
                  initialValue={keyValueChecked["finalstatuspaymentlink"]}
                />
              )
            }
          ]
        },
        {
          titleGroup: "PasswordConfiguration",
          childrenGroup: [
            {
              name: "PasswordConfiguration",
              component: () => (
                <PasswordConfiguration
                  pwdUpper={keyValueChecked["pwdUpper"]}
                  pwdLower={keyValueChecked["pwdLower"]}
                  pwdNumbers={keyValueChecked["pwdNumbers"]}
                  pwdSymbols={keyValueChecked["pwdSymbols"]}
                  pwdLength={keyValueChecked["pwdLength"]}
                  pwdExpireDays={keyValueChecked["pwdExpireDays"]}
                  pwdUnableToReuseLastNPwds={
                    keyValueChecked["pwdUnableToReuseLastNPwds"]
                  }
                  pwdUnableToChangePwdForNDays={
                    keyValueChecked["pwdUnableToChangePwdForNDays"]
                  }
                />
              )
            }
          ]
        },
        {
          titleGroup: "OtherSettings",
          childrenGroup: otherChildrenGroup
        },
        {
          titleGroup: "PaymentMethods",
          childrenGroup: [
            {
              name: "PaymentMethods",
              component: () => (
                <PaymentMethods
                  paymentMethod={keyValueChecked["paymentMethod"]}
                />
              )
            }
          ]
        },
        {
          titleGroup: "Backup",
          childrenGroup: [
            {
              name: "Backup",
              component: () => <BackupConfiguration {...keyValueChecked} />
            }
          ]
        }
      ];
      optionsValues.push({
        titleGroup: "ReceiptTemplate",
        childrenGroup: [
          {
            name: "ReceiptTemplate",
            component: () => (
              <ReceiptTemplateConfiguration
                receiptTemplate={keyValueChecked["receiptTemplate"]}
                receiptNumber={keyValueChecked["receiptNumber"]}
              />
            )
          }
        ]
      });
    }
    return optionsValues;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasReceipt, keyValueChecked]);

  const refreshConfigValues = () => {
    CompanyService.getConfigsCompany().then((res: any) =>
      setConfigValues(res.configs)
    );
  };

  useEffect(() => {
    refreshConfigValues();
  }, []);

  return (
    <ConfigurationContext.Provider
      value={{ otherConfigurationList, configValues, refreshConfigValues }}
    >
      {children}
    </ConfigurationContext.Provider>
  );
};

export default ConfigurationContext;
